<script setup lang="ts">
/*
 * Unified Employer create/edit form (V2-FE-2).
 *
 * One form for every Employer Kind — main company, subsidiary, OS client, OS
 * vendor, OS project. Labels adapt to `kind` (CLAUDE/plan decision #7):
 *   - COMPANY / OS_CLIENT / OS_VENDOR → "Company full name" + "Company short name"
 *   - OS_PROJECT                      → "Project full name" + "Project short name"
 *                                       + a read-only Client field (the parent)
 *
 * `EmployeeIdFormat` defaults to `<ShortName>-00000` (V1 `EmployerFormV2`'s
 * five-digit form) and is editable. The form always persists
 * `Organization.{FullName, ShortName}` + the `Kind` discriminator.
 *
 * R3-W1 (company lifecycle), all gated on `kind === 'COMPANY'` so the
 * outsourcing forms are untouched:
 *   - the Email / Phone / Representative repeaters and Notes are editable on
 *     EDIT too (V1 parity), and an edit sends only what changed — the full
 *     list when a list changed (`PUT` is partial; an empty list clears);
 *   - a Parent company Select when the caller passes `parentOptions` (the
 *     owner only does so for a TenantAdmin, and never for the tenant's own
 *     company); on create it defaults to `defaultParentId` (the tenant root);
 *   - `idFormatLocked` makes the employee-ID format read-only once employees
 *     exist (decision D11) and keeps it out of the body;
 *   - `serverError` renders the server's refusal (RFC 9457 `detail`) inline.
 *
 * The form is presentation-only: it emits `submit` with a built
 * `EmployerRequest`. The owning surface (Organization → Profile editor, or
 * FE-3's Outsourcing tab) handles the create/update mutation + the Drawer.
 */
import { computed, reactive, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { Plus, Trash2 } from 'lucide-vue-next';
import { Button, FormField, IconButton, Select, TextInput } from '@/ui';
import {
  employerFullName,
  type BaseLocation,
  type Employer,
  type EmployerKind,
  type EmployerRequest,
} from '@/api/services/employers';
import BaseLocationField from './BaseLocationField.vue';

const props = withDefaults(
  defineProps<{
    /** Discriminator the form is creating/editing. Drives the labels. */
    kind: Exclude<EmployerKind, 'UNKNOWN'>;
    /** Existing employer when editing; omit for create. */
    employer?: Employer | null;
    /** For OS_PROJECT: the parent OS_CLIENT/OS_VENDOR id (sent as ParentId). */
    parentId?: string | null;
    /** For OS_PROJECT: the parent's display name (read-only Client field). */
    parentName?: string | null;
    /** Disables the submit button while the parent's mutation is in-flight. */
    saving?: boolean;
    /**
     * COMPANY only: the companies this one may sit under (already excluding
     * itself, its descendants and outsourcing kinds). `null` hides the Parent
     * Select (non-admins, the tenant's own company, outsourcing forms).
     */
    parentOptions?: Employer[] | null;
    /** COMPANY create only: the Parent Select's initial value (the tenant root). */
    defaultParentId?: string | null;
    /** COMPANY edit only: lock the employee-ID format (employees exist). */
    idFormatLocked?: boolean;
    /**
     * COMPANY edit only: why the format is locked, for the helper text —
     * `issued` (IDs were minted), `checking` (counters still loading) or
     * `unverified` (the counters read failed). Defaults to `issued`.
     */
    idFormatLockReason?: 'open' | 'issued' | 'checking' | 'unverified';
    /** The server's refusal of the last submit, shown above the buttons. */
    serverError?: string | null;
  }>(),
  {
    employer: null,
    parentId: null,
    parentName: null,
    saving: false,
    parentOptions: null,
    defaultParentId: null,
    idFormatLocked: false,
    idFormatLockReason: 'issued',
    serverError: null,
  },
);

const emit = defineEmits<{
  submit: [body: EmployerRequest];
  cancel: [];
  /** The ID-format lock could not be checked; the owner re-reads the counters. */
  'retry-lock-check': [];
}>();

const { t } = useI18n();

const isProject = computed(() => props.kind === 'OS_PROJECT');
const isEdit = computed(() => Boolean(props.employer));

// The Kind the form emits is always exactly the `kind` prop the caller pinned.
// "Create new company" (from the scope switcher) always passes `COMPANY`; the
// Outsourcing tab passes OS_CLIENT/OS_VENDOR/OS_PROJECT explicitly. There is no
// persona toggle on the company-create surface — a tenant creates clients/vendors
// from Organization → Outsourcing (gated on the provider claim), never here.
const resolvedKind = computed<Exclude<EmployerKind, 'UNKNOWN'>>(() => props.kind);

const isCompany = computed(() => props.kind === 'COMPANY');
// The richer "Create New Company" surface (matching v1's EmployerFormV2) — the
// embedded base-location map — renders ONLY when creating a plain COMPANY
// (BaseLocation is a create-only payload; a site is edited under Sites).
const isCompanyCreate = computed(() => isCompany.value && !isEdit.value);
const isCompanyEdit = computed(() => isCompany.value && isEdit.value);
/** The ID format may only be edited on create, or on an edit while unlocked. */
const idFormatReadonly = computed(() => isCompanyEdit.value && props.idFormatLocked);

// --- Parent company (COMPANY only, when the owner passes options) ----------
/** Select value for "None (top level)" — the Select can't hold `null` as a real option. */
const PARENT_NONE = '';
const showParent = computed(() => isCompany.value && props.parentOptions !== null);
const parentSelection = ref<string>(PARENT_NONE);
const parentSelectOptions = computed(() => [
  { value: PARENT_NONE, label: t('v2.organization.company.parent-none') },
  ...(props.parentOptions ?? []).map((e) => ({ value: e.id, label: employerFullName(e) })),
]);

interface FormState {
  fullName: string;
  shortName: string;
  industry: string;
  employeeIdFormat: string;
  website: string;
  address: string;
  contactName: string;
  contactEmail: string;
  contactPhone: string;
  notes: string;
}

function emptyState(): FormState {
  return {
    fullName: '',
    shortName: '',
    industry: '',
    employeeIdFormat: '',
    website: '',
    address: '',
    contactName: '',
    contactEmail: '',
    contactPhone: '',
    notes: '',
  };
}

const form = reactive<FormState>(emptyState());
// Tracks whether the user has hand-edited the EmployeeIdFormat — once they
// have, we stop auto-deriving it from the short name.
const idFormatTouched = ref(false);
const submitted = ref(false);

// --- Company repeaters + base location (v1 parity) ------------------------
// Repeatable Email / Phone / Representative rows, mirroring v1's
// EmployerFormV2. Reactive arrays so add/remove just splice. The base location
// is the structured `BaseLocation` from the embedded Leaflet picker.
const emails = ref<string[]>(['']);
const phones = ref<string[]>(['']);
const representatives = ref<string[]>(['']);
const baseLocation = ref<BaseLocation | null>(null);

type RepeaterKey = 'emails' | 'phones' | 'representatives';
const repeaters: Record<RepeaterKey, typeof emails> = { emails, phones, representatives };

function addRow(key: RepeaterKey): void {
  // Always append a fresh empty row. (The previous guard refused to add a row
  // while the last one was blank, which made the "add" button silently no-op on
  // a fresh form — the reported bug.) Blank rows are dropped at submit time.
  repeaters[key].value.push('');
}
/**
 * V1 parity (EmployerFormV2 `addRepresentative`, QA R3-W1-06): another
 * representative can only be added once the last row holds a name. The button
 * is disabled — not a silent no-op — so the reason is visible.
 */
const canAddRepresentative = computed(() => {
  const rows = representatives.value;
  return (rows[rows.length - 1] ?? '').trim() !== '';
});

function removeRow(key: RepeaterKey, index: number): void {
  const rows = repeaters[key];
  rows.value.splice(index, 1);
  if (rows.value.length === 0) rows.value.push('');
}

/** A list as a repeater's rows — at least one (blank) row. */
function rowsOf(list: readonly string[]): string[] {
  return list.length > 0 ? [...list] : [''];
}

/** Hydrate the form when an existing employer is supplied (edit mode). */
function hydrate(): void {
  const e = props.employer;
  if (!e) {
    Object.assign(form, emptyState());
    emails.value = [''];
    phones.value = [''];
    representatives.value = [''];
    baseLocation.value = null;
    idFormatTouched.value = false;
    submitted.value = false;
    parentSelection.value = props.defaultParentId ?? PARENT_NONE;
    return;
  }
  Object.assign(form, {
    fullName: e.name,
    shortName: e.shortName === e.name ? '' : e.shortName,
    industry: e.industry,
    employeeIdFormat: e.employeeIdFormat,
    website: e.website,
    address: e.address,
    contactName: e.primaryContact.name,
    contactEmail: e.primaryContact.email,
    contactPhone: e.primaryContact.phone,
    notes: e.notes,
  });
  emails.value = rowsOf(e.emails);
  phones.value = rowsOf(e.phoneNumbers);
  representatives.value = rowsOf(e.representatives);
  parentSelection.value = e.parentId ?? PARENT_NONE;
  idFormatTouched.value = Boolean(e.employeeIdFormat);
  submitted.value = false;
}

watch(() => props.employer, hydrate, { immediate: true });
// The create default arrives once the tenant list loads; adopt it until the
// user picks something else.
watch(
  () => props.defaultParentId,
  (next, prev) => {
    if (isEdit.value) return;
    if (parentSelection.value === (prev ?? PARENT_NONE))
      parentSelection.value = next ?? PARENT_NONE;
  },
);

// Auto-derive `<ShortName>-00000` until the operator overrides it.
watch(
  () => form.shortName,
  (short) => {
    if (idFormatTouched.value) return;
    const prefix = short.trim();
    form.employeeIdFormat = prefix ? `${prefix}-00000` : '';
  },
);

/**
 * V1 `EmployerFormV2` derivation: the first letter of every word of the full
 * name, upper-cased ("ZZ Cobalt Holdings" → "ZCH", "ZZ-QA-R3-1 Co" → "ZC").
 * @param fullName the full name.
 * @returns the initials, or '' for a blank name.
 */
function initialsOf(fullName: string): string {
  return fullName
    .split(/\s+/)
    .filter((word) => word !== '')
    .map((word) => (word[0] as string).toUpperCase())
    .join('');
}

/**
 * Company create (R3-F3): when the full name loses focus and the short name is
 * still empty, fill it with the full name's initials. The ID format then
 * follows from the short name as usual. A short name already typed is never
 * overwritten.
 */
function onFullNameBlur(): void {
  if (!isCompanyCreate.value || form.shortName.trim()) return;
  form.shortName = initialsOf(form.fullName);
}

function onIdFormatInput(v: string | number): void {
  idFormatTouched.value = true;
  form.employeeIdFormat = String(v);
}

// --- Validation ---
const fullNameError = computed(() =>
  submitted.value && !form.fullName.trim() ? t('v2.organization.form.errors.full-name') : '',
);
const emailError = computed(() => {
  if (!submitted.value) return '';
  const email = form.contactEmail.trim();
  if (email && !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email)) {
    return t('v2.organization.form.errors.email');
  }
  return '';
});
/** Per-row email errors for the repeater (blank rows are fine — they're dropped). */
const emailRowErrors = computed<string[]>(() =>
  emails.value.map((v) =>
    submitted.value && v.trim() && !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(v.trim())
      ? t('v2.organization.form.errors.email')
      : '',
  ),
);

// On the company-create surface the base location is required (v1 blocks submit
// until a location with a name + valid coordinates is set).
const baseLocationMissing = computed(() => isCompanyCreate.value && baseLocation.value === null);
const isValid = computed(
  () =>
    Boolean(form.fullName.trim()) &&
    !emailError.value &&
    !emailRowErrors.value.some(Boolean) &&
    !baseLocationMissing.value,
);

const fullNameLabel = computed(() =>
  isProject.value
    ? t('v2.organization.form.project-full-name')
    : t('v2.organization.form.company-full-name'),
);
const shortNameLabel = computed(() =>
  isProject.value
    ? t('v2.organization.form.project-short-name')
    : t('v2.organization.form.company-short-name'),
);
const idFormatHint = computed(() => {
  if (idFormatReadonly.value) {
    if (props.idFormatLockReason === 'checking')
      return t('v2.organization.company.id-format-checking');
    if (props.idFormatLockReason === 'unverified')
      return t('v2.organization.company.id-format-unverified');
    return t('v2.organization.company.id-format-locked');
  }
  if (isCompanyEdit.value) return t('v2.organization.company.id-format-open');
  return t('v2.organization.form.employee-id-format-hint');
});

/** Whether two string lists hold the same values in the same order. */
function sameList(a: readonly string[], b: readonly string[]): boolean {
  return a.length === b.length && a.every((v, i) => v === b[i]);
}

/** COMPANY edit: only the changed fields, so the partial PUT leaves the rest alone. */
function buildCompanyEditBody(e: Employer): EmployerRequest {
  const body: EmployerRequest = {
    Organization: {
      FullName: form.fullName.trim(),
      ShortName: form.shortName.trim() || undefined,
    },
    Kind: resolvedKind.value,
  };
  const scalar = (value: string, original: string): string | undefined =>
    value.trim() !== original.trim() ? value.trim() : undefined;
  const industry = scalar(form.industry, e.industry);
  if (industry !== undefined) body.Industry = industry;
  if (!props.idFormatLocked) {
    const idFormat = scalar(form.employeeIdFormat, e.employeeIdFormat);
    if (idFormat !== undefined) body.EmployeeIdFormat = idFormat;
  }
  const website = scalar(form.website, e.website);
  if (website !== undefined) body.Website = website;
  const address = scalar(form.address, e.address);
  if (address !== undefined) body.Address = address;
  const notes = scalar(form.notes, e.notes);
  if (notes !== undefined) body.Notes = notes;
  const contact = {
    Name: form.contactName.trim(),
    Email: form.contactEmail.trim(),
    Phone: form.contactPhone.trim(),
  };
  const c = e.primaryContact;
  if (contact.Name !== c.name || contact.Email !== c.email || contact.Phone !== c.phone) {
    body.PrimaryContact = contact;
  }
  const cleanEmails = emails.value.map((v) => v.trim()).filter(Boolean);
  const cleanPhones = phones.value.map((v) => v.trim()).filter(Boolean);
  const cleanReps = representatives.value.map((v) => v.trim()).filter(Boolean);
  if (!sameList(cleanEmails, e.emails)) body.Emails = cleanEmails;
  if (!sameList(cleanPhones, e.phoneNumbers)) body.PhoneNumbers = cleanPhones;
  if (!sameList(cleanReps, e.representatives)) body.Representatives = cleanReps;
  // A move only when the Select is shown and changed; '' = top level.
  if (showParent.value && parentSelection.value !== (e.parentId ?? PARENT_NONE)) {
    body.ParentId = parentSelection.value;
  }
  return body;
}

function onSubmit(): void {
  submitted.value = true;
  if (!isValid.value) return;
  if (isCompanyEdit.value && props.employer) {
    emit('submit', buildCompanyEditBody(props.employer));
    return;
  }
  const body: EmployerRequest = {
    Organization: {
      FullName: form.fullName.trim(),
      ShortName: form.shortName.trim() || undefined,
    },
    Kind: resolvedKind.value,
  };
  if (isProject.value && props.parentId) body.ParentId = props.parentId;
  if (isProject.value && form.fullName.trim()) body.ProjectName = form.fullName.trim();
  if (form.industry.trim()) body.Industry = form.industry.trim();
  if (form.employeeIdFormat.trim()) body.EmployeeIdFormat = form.employeeIdFormat.trim();
  if (form.website.trim()) body.Website = form.website.trim();
  if (form.address.trim()) body.Address = form.address.trim();
  if (form.notes.trim()) body.Notes = form.notes.trim();
  const contact = {
    Name: form.contactName.trim() || undefined,
    Email: form.contactEmail.trim() || undefined,
    Phone: form.contactPhone.trim() || undefined,
  };
  if (contact.Name || contact.Email || contact.Phone) body.PrimaryContact = contact;
  // Company-create extras: repeatable Emails / PhoneNumbers / Representatives +
  // the embedded base location (v1 "Create New Company" parity).
  if (isCompanyCreate.value) {
    const cleanEmails = emails.value.map((v) => v.trim()).filter(Boolean);
    const cleanPhones = phones.value.map((v) => v.trim()).filter(Boolean);
    const cleanReps = representatives.value.map((v) => v.trim()).filter(Boolean);
    if (cleanEmails.length) body.Emails = cleanEmails;
    if (cleanPhones.length) body.PhoneNumbers = cleanPhones;
    if (cleanReps.length) body.Representatives = cleanReps;
    if (baseLocation.value) body.BaseLocation = baseLocation.value;
    // Parent on create: the selection (defaults to the tenant root); "None
    // (top level)" omits ParentId (V1 W3-E).
    if (showParent.value && parentSelection.value !== PARENT_NONE) {
      body.ParentId = parentSelection.value;
    }
  }
  emit('submit', body);
}
</script>

<template>
  <form class="flex flex-col gap-5" data-testid="employer-form" @submit.prevent="onSubmit">
    <!-- OS_PROJECT: read-only Client (parent) field -->
    <FormField v-if="isProject" :label="t('v2.organization.form.client')">
      <template #default="{ id }">
        <TextInput
          :id="id"
          :model-value="parentName ?? ''"
          disabled
          data-testid="employer-form-client"
          :placeholder="t('v2.organization.form.client-none')"
        />
      </template>
    </FormField>

    <FormField :label="fullNameLabel" required :error="fullNameError">
      <template #default="{ id, describedBy, invalid }">
        <TextInput
          :id="id"
          v-model="form.fullName"
          :aria-describedby="describedBy"
          :aria-invalid="invalid"
          data-testid="employer-form-full-name"
          @blur="onFullNameBlur"
        />
      </template>
    </FormField>

    <FormField :label="shortNameLabel" :hint="t('v2.organization.form.short-name-hint')">
      <template #default="{ id }">
        <TextInput :id="id" v-model="form.shortName" data-testid="employer-form-short-name" />
      </template>
    </FormField>

    <!-- Projects inherit every other field from their parent client/vendor, so
         the project form is just the two names. Everything below is hidden for
         OS_PROJECT and only rendered for COMPANY / OS_CLIENT / OS_VENDOR. -->
    <template v-if="!isProject">
      <FormField :label="t('v2.organization.form.employee-id-format')" :hint="idFormatHint">
        <template #default="{ id, describedBy }">
          <TextInput
            :id="id"
            :model-value="form.employeeIdFormat"
            :disabled="idFormatReadonly"
            :aria-describedby="describedBy"
            data-testid="employer-form-id-format"
            @update:model-value="onIdFormatInput"
          />
        </template>
      </FormField>
      <!-- The lock check failed: offer an inline retry instead of a silent,
           permanent lock (R3-F2). -->
      <button
        v-if="idFormatReadonly && idFormatLockReason === 'unverified'"
        type="button"
        class="-mt-3 self-start text-sm font-medium text-primary hover:text-primary-700"
        data-testid="employer-form-id-format-retry"
        @click="emit('retry-lock-check')"
      >
        {{ t('v2.common.retry') }}
      </button>

      <FormField
        v-if="showParent"
        :label="t('v2.organization.company.parent')"
        :hint="t('v2.organization.company.parent-hint')"
      >
        <template #default="{ id }">
          <Select
            :id="id"
            v-model="parentSelection"
            :options="parentSelectOptions"
            :aria-label="t('v2.organization.company.parent')"
            data-testid="employer-form-parent"
          />
        </template>
      </FormField>

      <FormField :label="t('v2.organization.form.industry')">
        <template #default="{ id }">
          <TextInput :id="id" v-model="form.industry" data-testid="employer-form-industry" />
        </template>
      </FormField>

      <FormField :label="t('v2.organization.form.website')">
        <template #default="{ id }">
          <TextInput
            :id="id"
            v-model="form.website"
            type="url"
            placeholder="https://"
            data-testid="employer-form-website"
          />
        </template>
      </FormField>

      <FormField :label="t('v2.organization.form.address')">
        <template #default="{ id }">
          <TextInput :id="id" v-model="form.address" data-testid="employer-form-address" />
        </template>
      </FormField>

      <fieldset
        class="flex flex-col gap-4 rounded-xl border border-app-border p-4"
        data-testid="employer-form-primary-contact"
      >
        <legend class="px-1 text-sm font-semibold text-app-fg">
          {{ t('v2.organization.form.primary-contact') }}
        </legend>
        <FormField :label="t('v2.organization.form.contact-name')">
          <template #default="{ id }">
            <TextInput :id="id" v-model="form.contactName" />
          </template>
        </FormField>
        <div class="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <FormField :label="t('v2.organization.form.contact-email')" :error="emailError">
            <template #default="{ id, describedBy, invalid }">
              <TextInput
                :id="id"
                v-model="form.contactEmail"
                type="email"
                :aria-describedby="describedBy"
                :aria-invalid="invalid"
                data-testid="employer-form-contact-email"
              />
            </template>
          </FormField>
          <FormField :label="t('v2.organization.form.contact-phone')">
            <template #default="{ id }">
              <TextInput :id="id" v-model="form.contactPhone" type="tel" />
            </template>
          </FormField>
        </div>
      </fieldset>

      <!-- Company repeaters (v1 "Create New Company" + EmployerFormV2 edit
           parity): Email / Phone / Representative rows, on create AND edit. -->
      <template v-if="isCompany">
        <fieldset
          class="flex flex-col gap-3 rounded-xl border border-app-border p-4"
          data-testid="employer-form-emails"
        >
          <legend class="px-1 text-sm font-semibold text-app-fg">
            {{ t('v2.organization.company-form.emails') }}
          </legend>
          <div v-for="(_, i) in emails" :key="`email-${i}`" class="flex items-end gap-2">
            <FormField
              class="flex-1"
              :label="t('v2.organization.company-form.email-n', { n: i + 1 })"
              :error="emailRowErrors[i]"
            >
              <template #default="{ id, describedBy, invalid }">
                <TextInput
                  :id="id"
                  v-model="emails[i]"
                  type="email"
                  :aria-describedby="describedBy"
                  :aria-invalid="invalid"
                  :data-testid="`employer-form-email-${i}`"
                />
              </template>
            </FormField>
            <IconButton
              v-if="emails.length > 1"
              :label="t('v2.common.remove')"
              :data-testid="`employer-form-email-remove-${i}`"
              @click="removeRow('emails', i)"
            >
              <Trash2 class="h-4 w-4" aria-hidden="true" />
            </IconButton>
          </div>
          <button
            type="button"
            class="self-end text-sm font-medium text-primary hover:text-primary-700"
            data-testid="employer-form-email-add"
            @click="addRow('emails')"
          >
            <Plus class="mr-1 inline h-3.5 w-3.5" aria-hidden="true" />{{
              t('v2.organization.company-form.add-email')
            }}
          </button>
        </fieldset>

        <fieldset
          class="flex flex-col gap-3 rounded-xl border border-app-border p-4"
          data-testid="employer-form-phones"
        >
          <legend class="px-1 text-sm font-semibold text-app-fg">
            {{ t('v2.organization.company-form.phones') }}
          </legend>
          <div v-for="(_, i) in phones" :key="`phone-${i}`" class="flex items-end gap-2">
            <FormField
              class="flex-1"
              :label="t('v2.organization.company-form.phone-n', { n: i + 1 })"
            >
              <template #default="{ id }">
                <TextInput
                  :id="id"
                  v-model="phones[i]"
                  type="tel"
                  :data-testid="`employer-form-phone-${i}`"
                />
              </template>
            </FormField>
            <IconButton
              v-if="phones.length > 1"
              :label="t('v2.common.remove')"
              :data-testid="`employer-form-phone-remove-${i}`"
              @click="removeRow('phones', i)"
            >
              <Trash2 class="h-4 w-4" aria-hidden="true" />
            </IconButton>
          </div>
          <button
            type="button"
            class="self-end text-sm font-medium text-primary hover:text-primary-700"
            data-testid="employer-form-phone-add"
            @click="addRow('phones')"
          >
            <Plus class="mr-1 inline h-3.5 w-3.5" aria-hidden="true" />{{
              t('v2.organization.company-form.add-phone')
            }}
          </button>
        </fieldset>

        <fieldset
          class="flex flex-col gap-3 rounded-xl border border-app-border p-4"
          data-testid="employer-form-representatives"
        >
          <legend class="px-1 text-sm font-semibold text-app-fg">
            {{ t('v2.organization.company-form.representatives') }}
          </legend>
          <div v-for="(_, i) in representatives" :key="`rep-${i}`" class="flex items-end gap-2">
            <FormField
              class="flex-1"
              :label="t('v2.organization.company-form.representative-n', { n: i + 1 })"
            >
              <template #default="{ id }">
                <TextInput
                  :id="id"
                  v-model="representatives[i]"
                  :data-testid="`employer-form-representative-${i}`"
                />
              </template>
            </FormField>
            <IconButton
              v-if="representatives.length > 1"
              :label="t('v2.common.remove')"
              :data-testid="`employer-form-representative-remove-${i}`"
              @click="removeRow('representatives', i)"
            >
              <Trash2 class="h-4 w-4" aria-hidden="true" />
            </IconButton>
          </div>
          <button
            type="button"
            class="self-end text-sm font-medium text-primary hover:text-primary-700 disabled:cursor-not-allowed disabled:text-app-muted"
            data-testid="employer-form-representative-add"
            :disabled="!canAddRepresentative"
            @click="addRow('representatives')"
          >
            <Plus class="mr-1 inline h-3.5 w-3.5" aria-hidden="true" />{{
              t('v2.organization.company-form.add-representative')
            }}
          </button>
        </fieldset>

        <FormField :label="t('v2.organization.form.notes')">
          <template #default="{ id }">
            <textarea
              :id="id"
              v-model="form.notes"
              rows="3"
              class="w-full rounded-lg border border-app-border bg-app-surface px-3 py-2 text-sm text-app-fg"
              data-testid="employer-form-notes"
            ></textarea>
          </template>
        </FormField>

        <BaseLocationField
          v-if="isCompanyCreate"
          v-model="baseLocation"
          required
          :show-error="submitted"
        />
      </template>
    </template>

    <p
      v-if="serverError"
      role="alert"
      class="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800"
      data-testid="employer-form-server-error"
    >
      {{ serverError }}
    </p>

    <div class="flex items-center justify-end gap-2 pt-1">
      <Button type="button" variant="outline" @click="emit('cancel')">
        {{ t('v2.common.cancel') }}
      </Button>
      <Button type="submit" variant="primary" :disabled="saving" data-testid="employer-form-submit">
        {{ isEdit ? t('v2.common.save') : t('v2.organization.form.create') }}
      </Button>
    </div>
  </form>
</template>
